// Phase 25 iter-7 — Pantalla destino dos correos /auth/forgot-password.
// Lee ?token=… do query string, pídea + nova password, chama
// /auth/reset-password e queda logueada coa TokenPair devolto.

import { Eye, EyeOff, Lock } from 'lucide-react';
import { useState } from 'react';
import type { FormEvent } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';

import { describeAxiosError } from '../api/errors';
import { confirmPasswordReset } from '../api/passwordReset';
import Spinner from '../components/ui/Spinner';
import { useAuthContext } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';

export default function ResetPasswordPage(): JSX.Element {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setSession } = useAuthContext();
  const { addToast } = useToast();
  const token = params.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  if (!token) {
    return <Navigate to="/marketplace/forgot-password" replace />;
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (password.length < 8) {
      setError('Mínimo 8 caracteres.');
      return;
    }
    if (password !== confirm) {
      setError('Os contrasinais non coinciden.');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const tokens = await confirmPasswordReset({ token, password });
      setSession(tokens);
      addToast('Contrasinal actualizado. Estás logueado.', 'success');
      navigate('/marketplace');
    } catch (err) {
      setError(describeAxiosError(err, 'O enlace caducou ou non é válido.'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="min-h-[60vh] flex items-center justify-center px-6 py-12 bg-subtle">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-card-hover p-8">
        <h1 className="text-2xl font-bold text-dark mb-2">Restablece o teu contrasinal</h1>
        <p className="text-sm text-muted leading-relaxed mb-5">
          Crea un novo contrasinal de polo menos 8 caracteres. Cando confirme, terás a sesión aberta
          automaticamente.
        </p>
        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          <PasswordField
            label="Novo contrasinal"
            value={password}
            onChange={setPassword}
            show={showPw}
            onToggleShow={() => setShowPw((v) => !v)}
          />
          <PasswordField
            label="Confirmar contrasinal"
            value={confirm}
            onChange={setConfirm}
            show={showPw}
            onToggleShow={() => setShowPw((v) => !v)}
          />
          {error && <p className="text-sm text-rauxa">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full py-3 disabled:opacity-60"
          >
            {loading ? <Spinner size={18} className="mx-auto" /> : 'Establecer contrasinal'}
          </button>
        </form>
      </div>
    </main>
  );
}

function PasswordField({
  label,
  value,
  onChange,
  show,
  onToggleShow,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  show: boolean;
  onToggleShow: () => void;
}) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wide text-muted font-medium block mb-1">
        {label}
      </span>
      <div className="relative flex items-center border border-border rounded-xl px-4 py-3">
        <Lock size={16} className="text-muted shrink-0 mr-3" />
        <input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete="new-password"
          className="flex-1 outline-none text-sm text-dark placeholder-muted bg-transparent"
        />
        <button
          type="button"
          onClick={onToggleShow}
          className="text-muted hover:text-dark"
          aria-label={show ? 'Ocultar' : 'Amosar'}
        >
          {show ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
    </label>
  );
}
